import { Call, Device } from "@twilio/voice-sdk"
import { useCallback, useEffect, useRef, useState } from "react"

import {
  completeDialerSession,
  createDialerSession,
  getDialerCallByCallSid,
  setDialerCallDisposition,
  setDialerCallNote
} from "~/api/dialer"
import { getVoiceToken } from "~/api/voice"
import useGlobalStore from "~/store/global"

type DeviceStatus = "idle" | "registering" | "ready" | "error"

export function useVoiceDevice() {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const deviceRef = useRef<Device | null>(null)
  const callRef = useRef<Call | null>(null)
  const sessionIdRef = useRef<number | null>(null)
  const [status, setStatus] = useState<DeviceStatus>("idle")
  const [onCall, setOnCall] = useState(false)
  const [muted, setMuted] = useState(false)

  const fetchToken = useCallback(async () => {
    if (!tokenInfo) throw new Error("no token info available")
    const res = await getVoiceToken(tokenInfo)
    return res.data.token
  }, [tokenInfo])

  useEffect(() => {
    if (!tokenInfo?.tenant_id || !tokenInfo?.user_id) return
    let device: Device | null = null
    let cancelled = false

    const setup = async () => {
      try {
        setStatus("registering")
        const token = await fetchToken()
        if (cancelled) return
        device = new Device(token, { logLevel: 1 })
        deviceRef.current = device
        device.on("registered", () => setStatus("ready"))
        device.on("error", () => setStatus("error"))
        device.on("tokenWillExpire", async () => {
          try {
            device?.updateToken(await fetchToken())
          } catch {
            setStatus("error")
          }
        })
        await device.register()
      } catch {
        if (!cancelled) setStatus("error")
      }
    }
    setup()

    return () => {
      cancelled = true
      device?.destroy()
      deviceRef.current = null
    }
  }, [tokenInfo?.tenant_id, tokenInfo?.user_id, fetchToken])

  const makeCall = useCallback(
    async (
      to: string,
      params: Record<string, string> = {},
      peopleId?: string,
      callingHours?: { start: string; end: string }
    ) => {
      const device = deviceRef.current
      if (!device || !tokenInfo) return null

      if (!sessionIdRef.current) {
        try {
          const { data } = await createDialerSession(
            tokenInfo,
            false,
            callingHours
          )
          sessionIdRef.current = data?.data?.id ?? null
        } catch {
          sessionIdRef.current = null
        }
      }

      const call = await device.connect({
        params: {
          To: to,
          ...params,
          ...(sessionIdRef.current
            ? { session_id: String(sessionIdRef.current) }
            : {}),
          ...(peopleId ? { people_id: peopleId } : {}),
          user_id: String(tokenInfo.user_id)
        }
      })
      callRef.current = call
      setOnCall(true)
      setMuted(false)
      const end = () => {
        setOnCall(false)
        callRef.current = null
      }
      call.on("disconnect", end)
      call.on("cancel", end)
      call.on("reject", end)
      call.on("error", end)
      return call
    },
    [tokenInfo]
  )

  const hangup = useCallback(() => {
    callRef.current?.disconnect()
    callRef.current = null
    setOnCall(false)
  }, [])

  const toggleMute = useCallback(() => {
    const call = callRef.current
    if (!call) return
    const next = !muted
    call.mute(next)
    setMuted(next)
  }, [muted])

  const sendDigits = useCallback((digits: string) => {
    callRef.current?.sendDigits(digits)
  }, [])

  const TERMINAL_CALL_STATUSES = ["COMPLETED", "FAILED", "NO_ANSWER"]

  const watchCallStatus = useCallback(
    (
      callSid: string | null | undefined,
      onStatus: (status: string) => void
    ) => {
      if (!tokenInfo || !callSid) return () => {}

      let cancelled = false
      let lastStatus: string | null = null
      const poll = async () => {
        while (!cancelled) {
          try {
            const { data } = await getDialerCallByCallSid(tokenInfo, callSid)
            const status = data?.data?.status
            if (status && status !== lastStatus) {
              lastStatus = status
              onStatus(status)
            }
            if (status && TERMINAL_CALL_STATUSES.includes(status)) return
          } catch {
            // keep polling — a transient lookup failure shouldn't stop the watch
          }
          await new Promise((resolve) => setTimeout(resolve, 1000))
        }
      }
      poll()

      return () => {
        cancelled = true
      }
    },
    [tokenInfo]
  )

  const endSession = useCallback(async () => {
    if (!tokenInfo || !sessionIdRef.current) return
    const sessionId = sessionIdRef.current
    sessionIdRef.current = null
    try {
      await completeDialerSession(tokenInfo, sessionId)
    } catch {
      // best-effort — a failed completion call shouldn't block the UI flow
    }
  }, [tokenInfo])

  const logCall = useCallback(
    async (
      callSid: string | null | undefined,
      {
        disposition,
        notes,
        meetingAt
      }: {
        disposition?: string
        notes?: string
        meetingAt?: { date?: string; time?: string } | null
      }
    ) => {
      if (!tokenInfo || !callSid) return

      try {
        const { data } = await getDialerCallByCallSid(tokenInfo, callSid)
        const callId = data?.data?.id
        if (!callId) return

        if (disposition) {
          const meetingSetAt = meetingAt?.date
            ? new Date(
                `${meetingAt.date}T${meetingAt.time || "09:00"}`
              ).getTime()
            : undefined
          await setDialerCallDisposition(
            tokenInfo,
            callId,
            disposition,
            meetingSetAt
          )
        }
        if (notes) {
          await setDialerCallNote(tokenInfo, callId, notes)
        }
      } catch {
        // best-effort — a failed log shouldn't block the caller's UI flow
      }
    },
    [tokenInfo]
  )

  return {
    status,
    onCall,
    muted,
    makeCall,
    hangup,
    toggleMute,
    sendDigits,
    logCall,
    watchCallStatus,
    endSession
  }
}
